import { useState } from 'react'
import { Pressable, StyleSheet, Text, View } from 'react-native'
import { formatGBPFromPence } from '../format'
import { usePaywallTracking } from '../hooks/usePaywallTracking'
import { colors, fontFamily, fontSize, radius, shadow, space } from '../tokens'
import { trpc } from '../trpc'
import { ErrorNotice } from './ErrorNotice'
import { PaidAppealCTAPreview } from './PaidAppealCTAPreview'

type Props = {
  bandCheckId: string
}

// Mirror of the constant in App.tsx — kept in sync by hand to avoid pulling
// App.tsx into the component's dependency graph.
const PROMO_STORAGE_KEY = 'bandcheck.promo.v1'

const readPromoCode = (): string | undefined => {
  if (typeof window === 'undefined' || !window.sessionStorage) return undefined
  return window.sessionStorage.getItem(PROMO_STORAGE_KEY) ?? undefined
}

const BENEFITS = [
  'Every comparable home, with sale prices & floor areas',
  'Full per-paragraph reasoning behind the case',
  'A ready-to-send Band Review email for the Valuation Office',
  "Pre-filled VO 7455 form if you've moved in recently",
] as const

export const PaidAppealCTA = ({ bandCheckId }: Props) => {
  const pricing = trpc.bandCheck.getPricing.useQuery({ bandCheckId })
  const create = trpc.appeal.create.useMutation()
  const checkout = trpc.payments.createCheckoutSession.useMutation()
  const [redirectError, setRedirectError] = useState<string | null>(null)
  const [consent, setConsent] = useState(false)
  // Fires appeal.cta.viewed on mount + returns the paywall action beacons.
  const track = usePaywallTracking(bandCheckId)

  const promoCode = readPromoCode()

  // Emit the toggle from outside the setState updater so it fires once per click
  // (a side effect inside the updater can run twice under StrictMode).
  const toggleConsent = () => {
    const next = !consent
    setConsent(next)
    track.onConsentToggle(next)
  }

  const onPay = async () => {
    if (!consent) return
    track.onCheckoutClick(Boolean(promoCode))
    setRedirectError(null)
    try {
      const appeal = await create.mutateAsync({ bandCheckId })
      // When a promo code is stashed in sessionStorage, the server may
      // bypass Stripe and return our own post-payment URL (see
      // payments-router.ts). Either way we just redirect.
      // Reg 37 CCRs — required by the server, which stamps the timestamp on the
      // Appeal row. Truthful here because `consent` gates this handler above.
      // NOTE: this legacy in-app paywall keeps its checkbox; the marketing
      // paywall (the live purchase surface since Phase 2) dropped the checkbox
      // gate on 2026-08-20 in favour of inline copy above an unconditional
      // button. See marketing/src/widgets/InlinePaywall.tsx.
      const session = await checkout.mutateAsync({
        appealId: appeal.id,
        promoCode,
        acknowledgedCancellationWaiver: true,
      })
      if (typeof window !== 'undefined') {
        window.location.href = session.url
      }
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Could not start checkout.'
      track.onCheckoutFailed(reason)
      setRedirectError(reason)
    }
  }

  if (!pricing.data) {
    return (
      <View style={styles.card}>
        <Text style={styles.subtitle}>Loading appeal package price…</Text>
      </View>
    )
  }

  const isPending = create.isPending || checkout.isPending

  return (
    <View style={styles.card}>
      <View style={styles.body}>
        <Text style={styles.tag}>Appeal pack</Text>
        <Text style={styles.title}>
          We'll build your case for the Valuation Office (part of HMRC)
        </Text>
        <Text style={styles.subtitle}>
          Everything you need to challenge, ready to send. We assemble the evidence the Valuation
          Office actually expects — so you don't have to decode it.
        </Text>

        <View style={styles.benefits}>
          {BENEFITS.map((b) => (
            <View key={b} style={styles.benefitRow}>
              <Text style={styles.benefitTick}>✓</Text>
              <Text style={styles.benefitText}>{b}</Text>
            </View>
          ))}
        </View>

        <PaidAppealCTAPreview />

        <View style={styles.priceRow}>
          <Text style={styles.price}>{formatGBPFromPence(pricing.data.priceAmountPence)}</Text>
        </View>
        <Text style={styles.priceMeta}>
          One-time · Total price, no further charges · No subscription
        </Text>

        <View style={styles.guarantee}>
          <Text style={styles.guaranteeIcon}>🛡</Text>
          <View style={styles.guaranteeBody}>
            <Text style={styles.guaranteeBold}>
              Full refund if the Valuation Office rejects your challenge.
            </Text>
            <Text style={styles.guaranteeText}>
              Send us their written decision within 60 days and we refund the{' '}
              {formatGBPFromPence(pricing.data.priceAmountPence)} in full.
            </Text>
          </View>
        </View>

        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: consent }}
          accessibilityLabel="Build my pack now. The 14-day cooling-off period ends once it's delivered; the money-back guarantee is unaffected."
          onPress={toggleConsent}
          style={styles.consentRow}
        >
          <View style={[styles.checkbox, consent && styles.checkboxChecked]}>
            {consent ? <Text style={styles.checkboxTick}>✓</Text> : null}
          </View>
          <View style={styles.consentTextWrap}>
            {/* Kept verbatim in step with the marketing InlinePaywall — the two
                paywalls must not drift, since a buyer can meet either one. See
                that file for why the wording is shaped this way (Reg 37 CCRs
                2013 needs both the immediate-supply request and the
                cancellation-right acknowledgement to stay unmistakable). */}
            <Text style={styles.consentLabel}>
              Yes — build my pack now. Because it's made for you on demand, the 14-day cooling-off
              period ends once it's delivered.
            </Text>
            <Text style={styles.consentMeta}>
              Your money-back guarantee is unaffected — the refund promise above still applies in
              full.
            </Text>
          </View>
        </Pressable>

        {promoCode ? (
          <View style={styles.promoBadge}>
            <Text style={styles.promoBadgeText}>✓ Promo applied — Stripe will be skipped</Text>
          </View>
        ) : null}

        <Pressable
          accessibilityRole="button"
          disabled={isPending || !consent}
          onPress={onPay}
          style={[styles.button, (isPending || !consent) && styles.buttonDisabled]}
        >
          <Text style={styles.buttonText}>
            {isPending
              ? 'Redirecting…'
              : promoCode
                ? '✓  Generate my pack (free)'
                : `🔒  Pay ${formatGBPFromPence(pricing.data.priceAmountPence)} & generate my pack`}
          </Text>
        </Pressable>

        {redirectError ? (
          <Text style={styles.error}>{redirectError}</Text>
        ) : (
          <ErrorNotice error={create.error ?? checkout.error} onRetry={onPay} />
        )}
      </View>

      <View style={styles.secureFooter}>
        <Text style={styles.secureText}>
          🔒 Secure payment by <Text style={styles.secureBold}>Stripe</Text> · Cards & Apple Pay
        </Text>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  card: {
    marginTop: space[16],
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    ...shadow.card,
  },
  body: {
    padding: 26,
    paddingBottom: 0,
    gap: space[8],
  },
  tag: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.primary[700],
    fontFamily: fontFamily.body,
  },
  title: {
    fontSize: fontSize.h3,
    fontWeight: '700',
    color: colors.ink,
    letterSpacing: -0.4,
    fontFamily: fontFamily.body,
  },
  subtitle: {
    fontSize: 14,
    color: colors.muted,
    lineHeight: 22,
    marginBottom: space[8],
    fontFamily: fontFamily.body,
  },
  benefits: {
    gap: space[8],
    marginBottom: space[16],
  },
  benefitRow: {
    flexDirection: 'row',
    gap: 11,
    alignItems: 'flex-start',
  },
  benefitTick: {
    color: colors.positive,
    fontSize: 16,
    fontWeight: '800',
    lineHeight: 20,
    width: 18,
  },
  benefitText: {
    flex: 1,
    fontSize: 14,
    color: colors.ink,
    lineHeight: 20,
    fontFamily: fontFamily.body,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 10,
    marginTop: space[8],
    marginBottom: 6,
  },
  price: {
    fontSize: 44,
    fontWeight: '500',
    letterSpacing: -1,
    color: colors.ink,
    fontFamily: fontFamily.serif,
  },
  priceMeta: {
    fontSize: 13,
    color: colors.muted,
    marginBottom: space[16],
    fontFamily: fontFamily.body,
  },
  guarantee: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-start',
    padding: 14,
    borderRadius: radius.sm,
    backgroundColor: colors.positiveSoft,
    marginBottom: space[16],
  },
  guaranteeIcon: {
    fontSize: 20,
    lineHeight: 22,
    color: colors.positive,
  },
  guaranteeBody: { flex: 1, gap: 2 },
  guaranteeBold: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.positiveInk,
    lineHeight: 19,
    fontFamily: fontFamily.body,
  },
  guaranteeText: {
    fontSize: 13,
    color: colors.positiveInk,
    lineHeight: 19,
    fontFamily: fontFamily.body,
  },
  consentRow: {
    flexDirection: 'row',
    gap: 11,
    alignItems: 'flex-start',
    marginBottom: space[16],
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: radius.sm,
    borderWidth: 1.5,
    borderColor: colors.borderStrong,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  checkboxChecked: {
    backgroundColor: colors.positive,
    borderColor: colors.positive,
  },
  checkboxTick: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 16,
  },
  consentTextWrap: { flex: 1, gap: 4 },
  consentLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.ink,
    lineHeight: 19,
    fontFamily: fontFamily.body,
  },
  // Same weight as consentLabel, in the guarantee's positive ink — the
  // reassurance used to be muted grey while the disclosure was bold, which is
  // backwards at the moment of payment. Mirrors .ipw-consent-meta on marketing.
  consentMeta: {
    fontSize: 12.5,
    fontWeight: '600',
    color: colors.positiveInk,
    lineHeight: 18,
    fontFamily: fontFamily.body,
  },
  button: {
    height: 56,
    borderRadius: radius.md,
    backgroundColor: colors.positive,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space[16],
  },
  buttonDisabled: { backgroundColor: colors.borderStrong },
  promoBadge: {
    padding: 10,
    borderRadius: radius.sm,
    backgroundColor: colors.positiveSoft,
    marginBottom: space[8],
    alignItems: 'center',
  },
  promoBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.positiveInk,
    fontFamily: fontFamily.body,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16.5,
    fontWeight: '700',
    fontFamily: fontFamily.body,
  },
  secureFooter: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: colors.surface2,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    alignItems: 'center',
  },
  secureText: {
    fontSize: 12.5,
    color: colors.muted,
    fontFamily: fontFamily.body,
  },
  secureBold: { color: colors.ink, fontWeight: '700' },
  error: {
    color: colors.error[900],
    backgroundColor: colors.error[100],
    borderLeftWidth: 4,
    borderLeftColor: colors.error[700],
    padding: 12,
    borderRadius: radius.sm,
    fontSize: 14,
    marginBottom: space[16],
    fontFamily: fontFamily.body,
  },
})
